import { useEffect, useRef } from 'react';

// Extra room left under the page beyond the panel's own height, so the last
// thing on the page isn't flush against the panel's top edge.
const FLOATING_PANEL_CLEARANCE_PX = 16;

/**
 * Keeps room at the bottom of the page for a panel floating over the bottom
 * of the screen, so the guest can still scroll everything above it into view.
 *
 * While `active`, the page body gets bottom padding equal to the space the
 * panel takes up from the bottom of the screen (its height plus the gap it
 * floats above the edge), kept in step as it grows or shrinks (a court card
 * opened, hours added). The body's own padding comes back when the panel stops
 * floating or the component unmounts.
 *
 * @param {boolean} active - whether the panel is floating right now
 * @returns {{current: HTMLElement|null}} ref to attach to the floating panel
 */
export function useFloatingPanelSpace(active) {
  const panelRef = useRef(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!active || !panel || typeof document === 'undefined') return undefined;
    const { body } = document;
    const previous = body.style.paddingBottom;
    // offsetHeight and the CSS `bottom`, not getBoundingClientRect: the panel
    // slides in with a transform, which would be measured mid-animation.
    const apply = () => {
      const gap = parseFloat(window.getComputedStyle(panel).bottom) || 0;
      body.style.paddingBottom = `${Math.ceil(panel.offsetHeight + gap) + FLOATING_PANEL_CLEARANCE_PX}px`;
    };
    apply();
    // Older browsers without ResizeObserver keep the first measurement. The
    // window's own resize (rotation, browser bars) moves the panel too.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(apply) : null;
    observer?.observe(panel);
    window.addEventListener('resize', apply);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', apply);
      body.style.paddingBottom = previous;
    };
  }, [active]);

  return panelRef;
}

export default useFloatingPanelSpace;
